import {
  Autocomplete,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import InputHeader from '../../../components/InputHeader';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { Operator } from '../../../types/operator';
import { Role, User } from '../../../types/user';
import { isValidEmail } from '../../../utils/validation';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { useCurrentUser } from '../../../contexts/currentUser';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import FormLayout, {
  FormLayoutElement,
  FormLayoutInputElement,
} from '../../../components/FormLayout';

const NAMES_FIELD_MAXIMUM_SIZE = 64;

export type UserFormValues = {
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  operatorId: string;
};

export interface UserFormProps {
  onSubmit: (values: UserFormValues, setAsreadOnly: () => void) => void;
  onCancel?: (setAsReadOnly: () => void) => void;
  isEditingByDefault?: boolean;
  isLoading: boolean;
  isResendingInvitation?: boolean;
  user?: User | null;
  onResendInvitationRequest?: (user: User) => void;
  onFullNameChange?: (fullName: string) => void;
  disabled?: boolean;
}

const UserForm = ({
  onSubmit,
  onCancel,
  onFullNameChange,
  isEditingByDefault = false,
  isLoading,
  isResendingInvitation = false,
  onResendInvitationRequest,
  disabled = false,
  user = null,
}: UserFormProps) => {
  const { t } = useLanguage();
  const { getOperators } = useIotsApi();

  const {
    data: { operators = [], isCurrentUserOperatorDeleted = false } = {},
    isFetching: isFetchingOperators,
    error: operatorsError,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: (operators: Operator[]) => {
      const selectableOperators = operators.filter(
        ({ id, isActive }) => isActive || id === user?.operatorId,
      );
      return {
        operators: selectableOperators,
        isCurrentUserOperatorDeleted: Boolean(
          user &&
            selectableOperators.find(
              ({ id, isActive }) => user.operatorId === id && !isActive,
            ),
        ),
      };
    },
  });

  useQueryErrorNotifier([operatorsError]);

  const { user: currentUser } = useCurrentUser();

  const initialValues = useMemo<UserFormValues>(() => {
    if (!user) {
      return {
        firstName: '',
        lastName: '',
        role: '',
        email: '',
        operatorId: '',
      };
    }
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      operatorId: user.operatorId ?? '',
    };
  }, [user]);

  const isUserInactive = Boolean(user && !user.isActive);
  const areInputsDisabled = disabled || isUserInactive || isLoading;

  const {
    formState,
    materialInputHelpers: {
      getBaseProps,
      getTextFieldProps,
      getSelectProps,
      renderError,
    },
  } = useIotsAdminForm(
    {
      isEditingByDefault,
      initialValues,
      onSubmit: (values, callback) => {
        onSubmit(
          {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            role: values.role,
            operatorId: values.operatorId,
          },
          callback,
        );
      },
      onEditionCancel: onCancel,
      validate: (values, setError) => {
        const firstName = values.firstName.trim();
        if (!firstName) {
          setError('firstName', t('validation.required-field'));
        } else if (firstName.length > NAMES_FIELD_MAXIMUM_SIZE) {
          setError(
            'firstName',
            t('validation.text-maximum-size', {
              SIZE: NAMES_FIELD_MAXIMUM_SIZE.toString(),
            }),
          );
        }

        const lastName = values.lastName.trim();
        if (!lastName) {
          setError('lastName', t('validation.required-field'));
        } else if (lastName.length > NAMES_FIELD_MAXIMUM_SIZE) {
          setError(
            'lastName',
            t('validation.text-maximum-size', {
              SIZE: NAMES_FIELD_MAXIMUM_SIZE.toString(),
            }),
          );
        }

        if (
          !values.role ||
          !Object.values(Role).includes(values.role as Role)
        ) {
          setError('role', t('validation.invalid-option'));
        } else if (
          (values.role as Role) === Role.OPERATOR &&
          !values.operatorId
        ) {
          setError('operatorId', t('validation.required-field'));
        } else if (
          isCurrentUserOperatorDeleted &&
          values.operatorId === user?.operatorId
        ) {
          setError('operatorId', t('validation.invalid-option'));
        }

        if (!isValidEmail(values.email.trim())) {
          setError('email', t('validation.invalid-option'));
        }
      },
      resetOnInitialValuesChange: () => !!user,
    },
    { shouldInputsBeDisabled: areInputsDisabled },
  );

  const { isCurrentUser } = useMemo(() => {
    return {
      isCurrentUser: Boolean(user && user.id === currentUser.id),
    };
  }, [currentUser, user]);

  useEffect(() => {
    onFullNameChange?.(
      [formState.values.firstName.trim(), formState.values.lastName.trim()]
        .filter(Boolean)
        .join(' '),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.firstName, formState.values.lastName]);

  return (
    <CommonMainPanelForm
      onCancelEdition={formState.cancelEdition}
      onEditionRequest={formState.requestEdition}
      onSubmit={formState.handleSubmit}
      isSubmitting={isLoading}
      isSubmitDisabled={
        formState.hasVisibleErrors || !formState.haveValuesChanged || isLoading
      }
      isEditDisabled={isLoading}
      isEditing={formState.isEditing}
    >
      <FormLayout>
        <FormLayoutInputElement required label={t('content.first-name')}>
          <TextField
            {...getTextFieldProps('firstName')}
            placeholder={t('content.first-name')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.last-name')}>
          <TextField
            {...getTextFieldProps('lastName')}
            placeholder={t('content.last-name')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.role')}>
          <FormControl error={getBaseProps('role').error}>
            <Select
              {...getSelectProps('role')}
              displayEmpty
              renderValue={value =>
                !value ? t('content.select') : t(`role.${value}`)
              }
              disabled={getBaseProps('role').disabled || isCurrentUser}
            >
              <MenuItem value={Role.ADMIN}>{t(`role.${Role.ADMIN}`)}</MenuItem>
              <MenuItem value={Role.DRCD}>{t(`role.${Role.DRCD}`)}</MenuItem>
              <MenuItem value={Role.OPERATOR}>
                {t(`role.${Role.OPERATOR}`)}
              </MenuItem>
            </Select>
            {renderError('role')}
          </FormControl>
        </FormLayoutInputElement>
        <FormLayoutElement className="flex gap-4 items-end">
          <label className="block flex-grow flex-wrap">
            <InputHeader required>{t('content.email-address')}</InputHeader>
            <TextField
              {...getTextFieldProps('email')}
              disabled={getBaseProps('email').disabled || !!user}
              placeholder={t('content.email-address')}
            />
          </label>
          {user && onResendInvitationRequest && (
            <Button
              variant="outlined"
              type="button"
              disabled={areInputsDisabled}
              loading={isResendingInvitation}
              onClick={() => onResendInvitationRequest(user)}
            >
              {t('content.resend-invitation')}
            </Button>
          )}
        </FormLayoutElement>
        {(formState.values.role as Role) === Role.OPERATOR && (
          <FormLayoutInputElement required label={t('content.operator')}>
            <FormControl error={getBaseProps('operatorId').error}>
              <Autocomplete<Operator>
                {...getBaseProps('operatorId')}
                value={operators.find(
                  ({ id }) => id === formState.values.operatorId,
                )}
                onChange={(_event, selectedOption) =>
                  formState.updateValues(
                    { operatorId: selectedOption?.id ?? '' },
                    true,
                  )
                }
                options={operators}
                loading={isFetchingOperators}
                disabled={
                  getBaseProps('operatorId').disabled || isFetchingOperators
                }
                renderInput={params => <TextField {...params} />}
                getOptionLabel={operator =>
                  `${operator.name}${operator.isActive ? '' : ` (${t('content.inactive')})`}`
                }
                getOptionDisabled={option => !option.isActive}
              />
              {renderError('operatorId')}{' '}
            </FormControl>
          </FormLayoutInputElement>
        )}
      </FormLayout>
    </CommonMainPanelForm>
  );
};

export default UserForm;
